import { adminUserSchema, paginated } from '@hearth/shared';
import { useQuery } from '@tanstack/react-query';
import { Link, createFileRoute } from '@tanstack/react-router';
import { ChevronRight, LayoutDashboard, ServerCog, ShieldCheck, UserX, Users } from 'lucide-react';
import { EmptyState, PageHeader, SectionHeader } from '@/components/page-header';
import { QueryState } from '@/components/query-state';
import { NodeHealthBadge } from '@/components/status-badges';
import { SystemStatus } from '@/components/system-status';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { UsageBar } from '@/components/usage-bar';
import { apiRequest } from '@/lib/api';
import { nodesQuery } from '@/lib/admin-queries';

export const Route = createFileRoute('/_app/admin/')({ component: AdminOverview });

function useUserCount(filter: 'all' | 'admins' | 'suspended') {
  return useQuery({
    queryKey: ['admin', 'users', 'count', filter],
    queryFn: ({ signal }) =>
      apiRequest(`/api/application/users?perPage=1&filter=${filter}`, {
        schema: paginated(adminUserSchema),
        signal,
      }),
    select: (d) => d.meta.total,
  });
}

function AdminOverview() {
  const counts = [
    { label: 'Users', icon: Users, q: useUserCount('all'), filter: undefined },
    { label: 'Administrators', icon: ShieldCheck, q: useUserCount('admins'), filter: 'admins' },
    { label: 'Suspended', icon: UserX, q: useUserCount('suspended'), filter: 'suspended' },
  ] as const;
  const nodes = useQuery(nodesQuery);
  return (
    <>
      <PageHeader
        title="Overview"
        icon={LayoutDashboard}
        description="Panel health, nodes and a summary of accounts."
      />
      <div className="grid gap-8">
        <section aria-label="System health">
          <SectionHeader title="System health" description="Checked every 10 seconds." />
          <SystemStatus />
        </section>

        <section aria-label="Accounts">
          <SectionHeader title="Accounts" />
          <div className="grid gap-3 sm:grid-cols-3">
            {counts.map(({ label, icon: Icon, q, filter }) => (
              <Link
                key={label}
                to="/admin/users"
                search={{ filter }}
                className="group focus-visible:ring-ring/50 rounded-xl outline-none focus-visible:ring-[3px]"
              >
                <Card className="group-hover:border-primary/40 gap-1 px-4 py-4 transition-colors">
                  <p className="text-muted-foreground flex items-center gap-2 text-sm">
                    <Icon className="text-subtle size-4" aria-hidden /> {label}
                    <ChevronRight
                      className="text-subtle ml-auto size-4 opacity-0 transition-opacity group-hover:opacity-100"
                      aria-hidden
                    />
                  </p>
                  <p className="text-2xl font-semibold tracking-tight tabular-nums">
                    {q.isError ? '—' : (q.data ?? '…')}
                  </p>
                </Card>
              </Link>
            ))}
          </div>
        </section>

        <section aria-label="Nodes">
          <SectionHeader
            title="Nodes"
            description="Daemon reachability and allocated capacity."
            actions={
              <Button asChild variant="outline" size="sm">
                <Link to="/admin/nodes">Manage nodes</Link>
              </Button>
            }
          />
          <QueryState query={nodes}>
            {(list) =>
              list.length === 0 ? (
                <EmptyState
                  icon={ServerCog}
                  title="No nodes yet"
                  action={
                    <Button asChild size="sm">
                      <Link to="/admin/nodes/new">Add a node</Link>
                    </Button>
                  }
                >
                  A node is a machine running hearthd. Servers can’t be created until one exists.
                </EmptyState>
              ) : (
                <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
                  {list.map((n) => (
                    <Link
                      key={n.id}
                      to="/admin/nodes/$nodeId"
                      params={{ nodeId: String(n.id) }}
                      className="group focus-visible:ring-ring/50 rounded-xl outline-none focus-visible:ring-[3px]"
                    >
                      <Card className="group-hover:border-primary/40 h-full gap-4 px-4 py-4 transition-colors">
                        <div className="flex items-start justify-between gap-2">
                          <div className="min-w-0">
                            <h3 className="truncate font-semibold tracking-tight">{n.name}</h3>
                            <p className="text-muted-foreground truncate font-mono text-xs">
                              {n.fqdn} · {n.location.short}
                            </p>
                          </div>
                          <NodeHealthBadge health={n.health} />
                        </div>
                        <div className="grid gap-3">
                          <UsageBar label="Memory" used={n.allocated.memoryMb} total={n.memoryMb} />
                          <UsageBar label="Disk" used={n.allocated.diskMb} total={n.diskMb} />
                        </div>
                        <p className="text-muted-foreground text-xs">
                          {n.serverCount} {n.serverCount === 1 ? 'server' : 'servers'} ·{' '}
                          {n.allocationCount}{' '}
                          {n.allocationCount === 1 ? 'allocation' : 'allocations'}
                          {n.maintenanceMode && (
                            <span className="text-warning"> · Maintenance mode</span>
                          )}
                        </p>
                      </Card>
                    </Link>
                  ))}
                </div>
              )
            }
          </QueryState>
        </section>
      </div>
    </>
  );
}
